import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MiniStat } from '../components/MiniStat';
import { Panel } from '../components/Panel';
import { PixelButton } from '../components/PixelButton';
import { Triangle } from '../components/Triangle';
import { currentStreak } from '../lib/streak';
import { dayKey, endOfMonth, startOfMonth } from '../lib/date';
import { useStore } from '../lib/store';

const MONTHS = [
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
];

export function CalendarScreen() {
  const nav = useNavigate();
  const { goals, checkInIndex } = useStore();
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));

  const now = new Date();
  const todayStr = dayKey(now);
  const habits = goals.filter(g => g.type === 'habit' && !g.archived_at);
  const habitCount = habits.length;

  const scheduledByDay = useMemo(() => {
    const m = new Map<string, number>();
    for (const g of goals) {
      if (g.type !== 'todo') continue;
      if (g.archived_at || g.completed_at) continue;
      if (!g.due_date) continue;
      m.set(g.due_date, (m.get(g.due_date) ?? 0) + 1);
    }
    return m;
  }, [goals]);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const startDow = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();

  const grid = useMemo(() => {
    const cells: ({ d: number; key: string; full: boolean; partial: boolean; today: boolean; scheduled: number } | null)[] = [];
    const total = 42;
    for (let i = 0; i < total; i++) {
      const dayNum = i - startDow + 1;
      if (dayNum < 1 || dayNum > daysInMonth) {
        cells.push(null);
        continue;
      }
      const d = new Date(cursor.getFullYear(), cursor.getMonth(), dayNum);
      const key = dayKey(d);
      const scheduled = scheduledByDay.get(key) ?? 0;
      if (d > now) {
        cells.push({ d: dayNum, key, full: false, partial: false, today: false, scheduled });
        continue;
      }
      let hit = 0;
      for (const g of habits) {
        if (checkInIndex.get(g.id)?.has(key)) hit++;
      }
      const full = habitCount > 0 && hit === habitCount;
      const partial = hit > 0 && !full;
      cells.push({ d: dayNum, key, full, partial, today: key === todayStr, scheduled });
    }
    return cells;
  }, [checkInIndex, cursor, daysInMonth, habits, habitCount, now, scheduledByDay, startDow, todayStr]);

  const monthStats = useMemo(() => {
    let full = 0;
    let partial = 0;
    let checkIns = 0;
    for (const c of grid) {
      if (!c) continue;
      if (c.full) full++;
      else if (c.partial) partial++;
      for (const g of habits) {
        if (checkInIndex.get(g.id)?.has(c.key)) checkIns++;
      }
    }
    const bestRun = habits.reduce(
      (m, g) => Math.max(m, currentStreak(checkInIndex, g.id, now)),
      0,
    );
    return { full, partial, checkIns, bestRun };
  }, [grid, habits, checkInIndex, now]);

  const shift = (delta: number) =>
    setCursor(c => new Date(c.getFullYear(), c.getMonth() + delta, 1));

  return (
    <div className="grid-calendar">
      <Panel title="CALENDAR">
        <div className="cal-header">
          <PixelButton onClick={() => shift(-1)} aria-label="previous month">
            <Triangle direction="left" />
          </PixelButton>
          <div className="cal-header__title">
            {MONTHS[cursor.getMonth()]} · {cursor.getFullYear()}
          </div>
          <PixelButton onClick={() => shift(1)} aria-label="next month">
            <Triangle direction="right" />
          </PixelButton>
        </div>
        <div className="calendar-grid">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
            <div key={i} className="cal-dow">{d}</div>
          ))}
          {grid.map((c, i) => {
            if (!c) return <div key={i} className="cal-cell cal-cell--empty" />;
            const cls = c.today
              ? 'cal-cell--today'
              : c.full
              ? 'cal-cell--full'
              : c.partial
              ? 'cal-cell--partial'
              : '';
            const clickable = c.scheduled > 0;
            return (
              <div
                key={i}
                className={`cal-cell ${cls}${clickable ? ' cal-cell--clickable' : ''}`}
                {...(clickable
                  ? {
                      onClick: () => nav(`/quests?due=${c.key}`),
                      role: 'button',
                      tabIndex: 0,
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          nav(`/quests?due=${c.key}`);
                        }
                      },
                      'aria-label': `${c.scheduled} scheduled on ${c.key}`,
                    }
                  : {})}
              >
                {c.d}
                {c.full && <span className="mark">✓</span>}
                {c.partial && !c.full && <span className="mark">~</span>}
                {c.today && <span className="mark">◆</span>}
                {c.scheduled > 0 && (
                  <span className="cal-cell__scheduled">×{c.scheduled}</span>
                )}
              </div>
            );
          })}
        </div>
      </Panel>

      <div className="screens-col">
        <Panel title="LEGEND">
          <div className="legend">
            {[
              { c: '#b83a26', l: 'all streaks kept' },
              { c: '#d9622c', l: 'partial day' },
              { c: '#f5e4b8', l: 'rest / missed' },
              { c: '#e5a42b', l: 'today' },
              { c: '#c98c28', l: 'scheduled errands' },
            ].map(x => (
              <div key={x.l} className="legend__row">
                <span className="legend__swatch" style={{ background: x.c }} />
                {x.l}
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="THIS MONTH">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <MiniStat label="FULL DAYS" value={String(monthStats.full)} />
            <MiniStat label="PARTIAL" value={String(monthStats.partial)} />
            <MiniStat label="BEST RUN" value={`${monthStats.bestRun}d`} icon="dot" streak={monthStats.bestRun} />
            <MiniStat label="CHECK-INS" value={String(monthStats.checkIns)} />
          </div>
        </Panel>
      </div>
    </div>
  );
}
